import { ENTITY, UserActions } from '@/enums';
import { useFetchRoles, useParseQueryString, useQueryNavigate } from '@/hooks';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { QueryParams } from '@/interfaces';
import { useToggle } from '@/utils';
import {
  CustomButton,
  CustomCheckbox,
  CustomCheckboxGroup,
  CustomFilter,
  Icon,
} from '@amili-v3/amili-v3-design';
import CustomScroll from '@amili-v3/amili-v3-design/src/components/Scrollbar';
import { Divider } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { CheckboxOptionType, CheckboxValueType } from 'antd/es/checkbox/Group';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterContent, FilterHeader } from '../styles';
import { FilterBody, FilterRolesWrapper } from './styles';

const AdminFilter = () => {
  const { t, i18n } = useTranslation();
  const handleNavigate = useQueryNavigate();
  const { data: allRoles } = useFetchRoles();
  const qsParams = useParseQueryString<QueryParams>();

  const { canGetUsers } = useCheckScopeCombinations([
    [{ entity: ENTITY.USERS, action: UserActions.GET }],
  ]);
  const canFilterSuperAdmin = true;
  const canFilterAdmin = true;
  const canFilterOperation = true;
  const canFilterLab = true;

  const filterOptions = useMemo(
    () =>
      allRoles.reduce((prev: CheckboxOptionType[], curr) => {
        if (canGetUsers) {
          return [
            ...prev,
            {
              value: curr.id,
              label: i18n.exists(`roles.${curr.name}`)
                ? t(`roles.${curr.name}`)
                : curr.name,
            },
          ];
        }
        return prev;
      }, []),
    [allRoles, t, canGetUsers, i18n],
  );

  const [open, toggleOpen] = useToggle();

  const initialValues = useMemo(() => {
    if (!qsParams.roles) return [];
    return (
      (qsParams?.roles?.map(role => Number(role)) as CheckboxValueType[]) || []
    );
  }, [qsParams]);

  const [indeterminate, setIndeterminate] = useState(
    initialValues.length > 0 && initialValues.length < filterOptions.length,
  );

  const [checkAll, setCheckAll] = useState(
    initialValues.length === filterOptions.length,
  );
  const [checkedList, setCheckedList] =
    useState<CheckboxValueType[]>(initialValues);

  const handleChange = (list: CheckboxValueType[]) => {
    setCheckedList(list);
    setIndeterminate(!!list.length && list.length < filterOptions.length);
    setCheckAll(list.length === filterOptions.length);
  };

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    setCheckedList(
      e.target?.checked ? filterOptions.map((option: any) => option.value) : [],
    );
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  const [rotate, setRotate] = useState(false);

  const onResetFilter = useCallback(() => {
    setCheckedList([]);
    setIndeterminate(false);
    setCheckAll(true);
    handleNavigate({ page: 1 }, ['roles']);

    setRotate(true);
    setTimeout(() => {
      setRotate(false);
    }, 300);
    toggleOpen();
  }, [handleNavigate, toggleOpen]);

  return (
    <CustomFilter
      open={open}
      quantity={qsParams?.roles?.length || 0}
      onOpenChange={toggleOpen}
      dropdownRender={() => (
        <FilterContent>
          <FilterHeader>
            {t('common.filters')}
            <CustomButton type="white" onClick={onResetFilter}>
              <Icon name="refresh" width={15} height={15} rotateLeft={rotate} />
            </CustomButton>
          </FilterHeader>

          <Divider />

          <FilterBody>
            <div className="title">{t('common.role')}</div>
            {(canFilterSuperAdmin ||
              canFilterAdmin ||
              canFilterLab ||
              canFilterOperation) && (
              <CustomCheckbox
                indeterminate={indeterminate}
                onChange={onCheckAllChange}
                checked={checkAll}
              >
                {t('common.all')}
              </CustomCheckbox>
            )}
            <FilterRolesWrapper>
              <CustomScroll>
                <CustomCheckboxGroup
                  value={checkedList}
                  options={filterOptions}
                  onChange={handleChange}
                />
              </CustomScroll>
            </FilterRolesWrapper>

            <div className="footer">
              <CustomButton
                size="large"
                width={'100%'}
                onClick={() => {
                  handleNavigate({ page: 1, roles: checkedList as number[] });
                  toggleOpen();
                }}
              >
                {t('common.apply')}
              </CustomButton>
            </div>
          </FilterBody>
        </FilterContent>
      )}
    />
  );
};

export default AdminFilter;
